import { Instagram, Youtube, Facebook, Mail, LogIn } from 'lucide-react'
import { Link } from 'react-router-dom'
import { SlotImage } from '@/features/designer/components/SlotImage'
import { useImageSlot } from '@/features/designer/hooks/useImageSlot'
import type { SlotKey } from '@/features/designer/slotRegistry'
import { useUserRole } from '@/hooks/useUserRole'
import { useImageLuminance } from '../hooks/useImageLuminance'

interface SocialPill {
  href: string
  label: string
  Icon: typeof Instagram
  slotKey: SlotKey
  external: boolean
}

const pills: SocialPill[] = [
  {
    href: 'https://www.instagram.com/onedaydance/',
    label: 'Instagram',
    Icon: Instagram,
    slotKey: 'homepage-social-instagram',
    external: true,
  },
  {
    href: 'https://www.youtube.com/channel/UCNIdQBvGM_Z9Pbe-CNRp5Mw',
    label: 'YouTube',
    Icon: Youtube,
    slotKey: 'homepage-social-youtube',
    external: true,
  },
  {
    href: 'https://www.facebook.com/OneDayDanceGroup/',
    label: 'Facebook',
    Icon: Facebook,
    slotKey: 'homepage-social-facebook',
    external: true,
  },
  {
    href: 'mailto:Info@onedaydance.com',
    label: 'Email',
    Icon: Mail,
    slotKey: 'homepage-social-email',
    external: false,
  },
  {
    href: '/sign-in',
    label: 'Login',
    Icon: LogIn,
    slotKey: 'homepage-social-login',
    external: false,
  },
]

function SocialPillItem({ pill }: { pill: SocialPill }) {
  const { href, label, Icon, slotKey, external } = pill
  const { assignment } = useImageSlot(slotKey)
  const luminance = useImageLuminance(assignment?.mediaUrl)
  const { canDesign } = useUserRole()

  // Default to white when we can't sample (no image / CORS) — pairs with the gradient fallback.
  const isLight = luminance !== null && luminance > 0.6
  const textColor = isLight ? '#0a0a0a' : '#ffffff'
  const textShadow = isLight
    ? '0 1px 2px rgba(255,255,255,0.55)'
    : '0 1px 2px rgba(0,0,0,0.55)'

  const isInternalRoute = href.startsWith('/')
  const className =
    'group relative flex h-20 items-center overflow-hidden rounded-full border shadow-sm transition-shadow duration-300 hover:shadow-[0_0_24px_4px_#FF9D7673]'

  const pillBody = (
    <>
      <SlotImage
        slotKey={slotKey}
        alt=""
        className="absolute inset-0 h-full w-full"
        fallback={
          <div className="h-full w-full bg-gradient-to-r from-brand-orange via-brand-purple to-brand-blue" />
        }
      />

      {/* Overlay — starts at icon center, extends to right edge; tracks the icon as it slides */}
      <div
        className="pointer-events-none absolute inset-y-0 right-0 left-10 transition-all duration-500 ease-in-out group-hover:left-[calc(100%-2.5rem)]"
        style={{ backgroundColor: isLight ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)' }}
      />

      <span
        className="relative z-10 flex w-full items-center justify-center px-20 text-center text-2xl font-bold tracking-tight transition-colors duration-300"
        style={{ color: textColor, textShadow }}
      >
        {label}
      </span>

      <div className="pointer-events-none absolute left-0 top-0 z-20 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-brand-orange via-brand-purple to-brand-blue text-white shadow-md transition-all duration-500 ease-in-out group-hover:left-[calc(100%-5rem)]">
        <Icon className="h-7 w-7" strokeWidth={2} />
      </div>
    </>
  )

  return (
    <li>
      {canDesign ? (
        // In designer mode, render a non-link wrapper so clicks inside the
        // designer overlay/picker don't bubble up and navigate.
        <div className={className}>{pillBody}</div>
      ) : isInternalRoute ? (
        <Link to={href} aria-label={label} className={className}>
          {pillBody}
        </Link>
      ) : (
        <a
          href={href}
          target={external ? '_blank' : undefined}
          rel={external ? 'noopener noreferrer' : undefined}
          aria-label={label}
          className={className}
        >
          {pillBody}
        </a>
      )}
    </li>
  )
}

export function FollowUsContent() {
  return (
    <div>
      <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-orange">
        Follow Us
      </p>
      <h2 className="mb-8 text-3xl font-bold tracking-tight">Stay connected.</h2>

      <ul className="flex flex-col gap-4">
        {pills.map((pill) => (
          <SocialPillItem key={pill.label} pill={pill} />
        ))}
      </ul>
    </div>
  )
}

export function FollowUsSection() {
  return (
    <section className="border-t py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-3xl">
          <FollowUsContent />
        </div>
      </div>
    </section>
  )
}
